import { Link } from "react-router-dom";
import { ExternalLink, MapPin, Monitor } from "lucide-react";
import { useSeo } from "../lib/seo";
import { SERVICES } from "../data/content";
import { PageIntro } from "../components/site/Sections";
import { ServiceIcon } from "../components/site/Icons";

const DeliveryTag = ({ label }) => {
  const Icon = label === "Virtual" ? Monitor : MapPin;
  return (
    <span className="inline-flex items-center gap-1.5 font-sans text-sm bg-sky text-ink rounded-full px-3 py-1">
      <Icon className="w-3.5 h-3.5 text-teal" aria-hidden="true" />{label}
    </span>
  );
};

const ServiceBlock = ({ s, i }) => (
  <article id={s.slug} className={`scroll-mt-8 py-14 border-t border-ink/20 grid md:grid-cols-[auto_1fr_1fr] gap-6 md:gap-12`} data-testid={`service-${s.slug}`}>
    <span className="font-display italic text-3xl text-brass w-12" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
    <div className="min-w-0">
      <div className="flex gap-4"><ServiceIcon name={s.icon} /><h2 className="font-display !text-3xl md:!text-4xl">{s.title}</h2></div>
      <p className="mt-4 text-lg">{s.short}</p>
    </div>
    <div className="min-w-0">
      <p className="text-slate">{s.body}</p>
      <dl className="mt-6 space-y-3 font-sans text-[15px]">
        <div><dt className="lbl text-ink">Best for</dt><dd className="mt-1">{s.for}</dd></div>
        <div><dt className="lbl text-ink">Delivery</dt><dd className="mt-2 flex flex-wrap gap-2">{s.delivery.map((d) => <DeliveryTag key={d} label={d} />)}</dd></div>
      </dl>
      {s.link && (
        <a href={s.link.url} target="_blank" rel="noreferrer" className="mt-4 mr-6 inline-flex items-center gap-1.5 font-sans link-u text-teal" data-testid={`service-${s.slug}-external-link`}>
          {s.link.label} <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" /><span className="sr-only">(opens in a new tab)</span>
        </a>
      )}
      <Link to={`/contact?service=${encodeURIComponent(s.title)}#consultation`} className="mt-6 inline-flex font-sans link-u text-teal" data-testid={`service-${s.slug}-cta`}>Ask about this service</Link>
    </div>
  </article>
);

export default function Services() {
  useSeo("Services", "School consultation, D/HH program support, educational assessments, IEP consultation, professional development, Visual Phonics training, family guidance and direct instruction. In person or virtual.");
  return (
    <>
      <PageIntro eyebrow="Services" title="Support for every adult around the student" testid="services-intro">
        <p>Every service is available in person or virtually, depending on where you are. Licensed in Pennsylvania and working with clients worldwide; the best months for travel are June, July and August.</p>
      </PageIntro>
      <section className="bg-cream text-ink pb-16" aria-label="Service list">
        <div className="max-w-6xl mx-auto px-6">
          {SERVICES.map((s, i) => <ServiceBlock key={s.slug} s={s} i={i} />)}
          <article className="py-14 border-t border-ink/20" data-testid="service-guest-speaking">
            <h2 className="font-display !text-3xl md:!text-4xl">Training and guest speaking</h2>
            <p className="mt-4 max-w-3xl text-lg">Training and guest speaking in a variety of topic areas related to deaf education, for conferences, universities, districts and parent groups, live or virtual.</p>
          </article>
        </div>
      </section>
      <section className="bg-teal text-paper py-20" data-testid="services-cta">
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap items-center justify-between gap-8">
          <h2 className="font-display max-w-xl">Not sure where to start?</h2>
          <div className="flex flex-wrap gap-4 font-sans">
            <Link to="/contact#consultation" className="bg-paper text-ink rounded-full px-7 py-3.5 hover:bg-sky transition-colors" data-testid="services-consultation-cta">Request a consultation</Link>
            <Link to="/contact#quote" className="border-2 border-paper rounded-full px-6 py-3 hover:bg-paper hover:text-ink transition-colors" data-testid="services-quote-cta">Request a quote</Link>
          </div>
        </div>
      </section>
    </>
  );
}
